/**
 * 어댑터가 하던 prefill 을 문서 선언으로 옮긴 것.
 *
 * 각 선언은 레거시 어댑터의 `initialValues` 계산 한 덩어리와 1:1 대응한다. 어느 어댑터의
 * 어느 줄을 대체하는지 주석에 남겨 두었다 — 이관 중에는 두 경로가 공존하므로, 한쪽만
 * 고치는 실수를 막으려면 대응 관계가 보여야 한다.
 */

import type { DataSource } from '@miso/design-system';

/**
 * 상담 시작 — 출발지 주소·건물명 기본값.
 *
 * 대체 대상: `CounselStartPage.startInitialValues` (order 배정 주소 → `_prefillAddress`).
 *
 * 건물명이 채워지는 경로는 두 단계다:
 *   1. 여기서 만든 `_prefillAddress.buildingName` (AI 자동기입)
 *   2. 그게 비면 문서의 `initialValueFromFallback: 'roada'` 가 도로명주소 검색 결과의
 *      건물명을 쓴다 — 이건 이미 v1 문서에 있고 v2 로 그대로 넘어온다.
 *
 * 즉 이 선언은 1단계의 **출처**를 문서로 드러낼 뿐, 폴백 규칙은 건드리지 않는다.
 */
export const DEPARTURE_PREFILL_DATA: DataSource[] = [
  { id: 'order', source: 'order.snapshot' },
  {
    id: '_prefillContext',
    derive: 'departure.prefillFromOrder',
    args: { location: { $var: 'order.assignedDepartureLocation' } },
  },
  // 문서 질문들이 `_prefillAddress.*` 경로로 읽으므로 한 겹 벗겨 스코프에 올린다.
  { id: '_prefillAddress', value: { $var: '_prefillContext._prefillAddress' } },
];

/**
 * 도착지 주소 — 이미 저장된 주소가 있으면 채워서 연다.
 *
 * 대체 대상: `ArrivalAddressStepAdapter.initialValues`
 * (`arrivalAddressToFlowValues(counsel.arrivalAddress)`).
 *
 * "주소가 이미 있으면 이 단계를 아예 건너뛴다" 는 별개 규칙이며 stage 머신
 * (`resolveProgressSubStage`) 소관이다. 여기는 **건너뛰지 않고 들어온 경우**
 * — 즉 리뷰에서 "수정" 으로 진입했을 때 — 기존 값이 보이게 하는 몫이다.
 */
export const ARRIVAL_ADDRESS_PREFILL_DATA: DataSource[] = [
  { id: 'counsel', source: 'counsel.snapshot' },
  // 결과가 질문 id 별 값 묶음이라 스코프에 그대로 편다 — 어댑터가 이 결과를
  // `initialValues` 로 통째로 넘기던 것과 같다.
  {
    id: '_prefill',
    derive: 'arrival.prefillFromCounsel',
    args: { address: { $var: 'counsel.arrivalAddress' } },
    spread: true,
  },
];

/**
 * 운반방식 — 저장된 값과 분기 답을 함께 복원한다.
 *
 * 대체 대상: `DepartureTransportStepAdapter` / `ArrivalTransportStepAdapter` 의
 * `movingMethodToFlowValues(...)`.
 *
 * `side` 로 출발/도착을 갈라 같은 선언을 재사용한다. 어댑터 두 개가 거의 같은 코드를
 * 들고 있던 것을 문서 인자 하나로 합쳤다.
 */
export function transportPrefillData(side: 'departure' | 'arrival'): DataSource[] {
  return [
    { id: 'counsel', source: 'counsel.snapshot' },
    {
      id: '_prefill',
      derive: 'transport.prefillFromCounsel',
      args: {
        movingMethod: { $var: `counsel.${side}MovingMethod` },
        hasElevator: { $var: `counsel.${side}HasElevator` },
      },
      spread: true,
    },
  ];
}
